import moment from 'moment';
import Text from '@deprecated-ui/common/Text';
import Link from 'next/link';
import {
  BondOrderStatus,
  BondTransactionActionEnum,
} from 'global/Securities/types/api/bond';
import If from 'global/ErrorHandlers/If';
import { InfoTable } from 'features/trade/components/DetailModal/InfoTable';
import { Flex } from 'features/securities/styled';
import { ChevronRightSvg } from 'features/day-trade/components/Icons';
import { formatPercentage } from 'utils/number';
import { useBondsModalStore } from '../../stores/useBondsModalStore';
import { useBondOrder } from '../../stores/useBondOrder';
import { ModalBody, ModalContainer, ModalHeader } from '../../styled';
import ModalTitle from '../ModalTitle';
import BondProductDisplay from '../BondProductDisplay';
import InfoPanel from './components/InfoPanel';
import { Container, OrderStatusLabel } from './styled';
import { BondOrderStatusLabelMap } from './constants';
import Timeline from './components/Timeline';
import OrderContentBuy from './components/OrderContentBuy';
import OrderContentSell from './components/OrderContentSell';

const getTitle = (
  productId = '',
  action: BondTransactionActionEnum = BondTransactionActionEnum.UNSPECIFIED,
) =>
  ({
    [BondTransactionActionEnum.BUY]: `Buy ${productId}`,
    [BondTransactionActionEnum.SELL]: `Sell ${productId}`,
    [BondTransactionActionEnum.UNSPECIFIED]: productId,
  }[action]);

export default function ModalBondOrder() {
  const [closeModal, closeTransactionModal] = useBondsModalStore((s) => [
    s.closeModal,
    s.closeTransactionModal,
  ]);
  const { selected } = useBondOrder((s) => ({
    selected: s.selected,
    isLoading: s.isLoading,
  }));

  const productId = selected?.product?.id;

  const action = selected?.action || BondTransactionActionEnum.BUY;
  const title = getTitle(productId, action);

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle title={title} onClose={closeModal} />
      </ModalHeader>
      <ModalBody
        css={`
          width: 502px;
        `}
      >
        <Container>
          <Link href={`/symbol/${selected?.product.id}`}>
            <Flex
              as="a"
              justify="space-between"
              align="center"
              onClickCapture={closeTransactionModal}
            >
              <BondProductDisplay product={selected?.product} />
              <ChevronRightSvg />
            </Flex>
          </Link>
        </Container>
        <InfoTable
          dataSource={[
            {
              label: <Text data-cy="bond-status-label">Status</Text>,
              value: (
                <OrderStatusLabel
                  isNegative={selected.status === BondOrderStatus.REJECTED}
                >
                  {BondOrderStatusLabelMap[
                    selected.status
                  ]?.toLocaleUpperCase()}
                </OrderStatusLabel>
              ),
            },
            {
              label: <Text data-cy="bond-order-id-label">Order ID</Text>,
              value: <Text data-cy="bond-order-id-content">{selected.id}</Text>,
            },
            {
              label: <Text data-cy="bond-order-time-label">Order Time</Text>,
              value: (
                <Text data-cy="bond-order-time-content">
                  {moment(selected?.ordered_at)
                    .utc()
                    .format('DD MMM YY, HH:mm:ss')}
                </Text>
              ),
            },
            {
              label: (
                <Text data-cy="bond-unit-label">
                  {action === BondTransactionActionEnum.BUY
                    ? 'Unit Pembelian'
                    : 'Unit Penjualan'}
                </Text>
              ),
              value: (
                <Text data-cy="coupon-distribution-content">
                  {selected?.trade_unit} Unit
                </Text>
              ),
            },
            {
              label: (
                <Text data-cy="bond-unit-label">
                  {action === BondTransactionActionEnum.BUY
                    ? 'Harga Beli (%)'
                    : 'Harga Jual (%)'}
                </Text>
              ),
              value: (
                <Text data-cy="coupon-distribution-content">
                  {formatPercentage(
                    selected?.price_rate.toString(),
                    false,
                    '0,0.00',
                    true,
                  )}
                </Text>
              ),
            },
          ]}
          title={
            action === BondTransactionActionEnum.BUY
              ? 'Buy Order'
              : 'Sell Order'
          }
        />

        <If condition={action === BondTransactionActionEnum.BUY}>
          <Container>
            <OrderContentBuy
              finalAmount={selected?.final_amount}
              sellerAccruedInterest={selected?.accrued_interest_amount}
              stampDuty={selected?.fee?.stamp_duty}
              tradeAmount={selected?.trade_amount}
            />
          </Container>
        </If>
        <If condition={action === BondTransactionActionEnum.SELL}>
          <OrderContentSell
            finalAmount={selected?.final_amount}
            tradeAmount={selected?.trade_amount}
            accruedInterest={selected?.fee?.accrued_interest_exclude_tax}
            totalTax={selected?.fee?.total_tax}
            stampDuty={selected?.fee?.stamp_duty}
            capitalGain={selected?.capital_gain_loss}
            profitLossAmount={selected?.profit_loss_nett.amount}
            profitLossPercentage={selected?.profit_loss_nett.percentage}
            sellerCoupon={selected?.seller_coupon}
            accruedInterestExcludeTax={
              selected?.fee?.accrued_interest_exclude_tax
            }
            estimationAmountExcludeTax={
              selected?.fee?.estimation_amount_exclude_tax
            }
            realizedAmount={selected?.realized_amount}
          />
        </If>

        <Container>
          <Timeline
            contents={selected?.status_timeline}
            message={selected?.reject_reason?.message}
          />
        </Container>
        <If
          condition={
            selected?.status !== BondOrderStatus.REJECTED &&
            selected?.status !== BondOrderStatus.SUCCEEDED
          }
        >
          <InfoPanel text="Estimasi pembelian kamu akan selesai dalam 2 sampai 3 hari kerja." />
        </If>
      </ModalBody>
    </ModalContainer>
  );
}
